/**
 * ApiResponseViewerEditor
 *
 * Property editor for api-response-viewer control type
 * Configures API response display, formatting, and code highlighting
 *
 * SRP: Responsible ONLY for configuring api-response-viewer properties
 * Control Type: 'api-response-viewer'
 *
 * Properties Configured (7):
 * 1. jsonIndentation - Spaces for JSON indentation
 * 2. showLineNumbers - Display line numbers
 * 3. showSearchBox - Display search/filter box
 * 4. theme - Syntax highlighting theme
 * 5. collapseLargeObjects - Auto-collapse large objects
 * 6. backgroundColor - Background color
 * 7. textColor - Text color
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { NumberPropertyEditor } from '../BaseEditors/NumberPropertyEditor';
import { CheckboxPropertyEditor } from '../BaseEditors/CheckboxPropertyEditor';
import { SelectPropertyEditor } from '../BaseEditors/SelectPropertyEditor';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';

/**
 * API response viewer configuration
 */
interface ApiResponseViewerConfig {
  jsonIndentation?: number;
  showLineNumbers?: boolean;
  showSearchBox?: boolean;
  theme?: 'light' | 'dark' | 'monokai';
  collapseLargeObjects?: boolean;
  backgroundColor?: string;
  textColor?: string;
}

/**
 * ApiResponseViewerEditor Component
 * Renders property editors for all api-response-viewer configuration
 */
const ApiResponseViewerEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => (control.config as ApiResponseViewerConfig) || {},
    [control.config],
  );

  const handleJsonIndentationChange = useCallback(
    (value: number) => onConfigChange('jsonIndentation', value),
    [onConfigChange],
  );

  const handleShowLineNumbersChange = useCallback(
    (value: boolean) => onConfigChange('showLineNumbers', value),
    [onConfigChange],
  );

  const handleShowSearchBoxChange = useCallback(
    (value: boolean) => onConfigChange('showSearchBox', value),
    [onConfigChange],
  );

  const handleThemeChange = useCallback(
    (value: string) => onConfigChange('theme', value as 'light' | 'dark' | 'monokai'),
    [onConfigChange],
  );

  const handleCollapseLargeObjectsChange = useCallback(
    (value: boolean) => onConfigChange('collapseLargeObjects', value),
    [onConfigChange],
  );

  const handleBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('backgroundColor', value),
    [onConfigChange],
  );

  const handleTextColorChange = useCallback(
    (value: string) => onConfigChange('textColor', value),
    [onConfigChange],
  );

  const themeOptions = useMemo(
    () => [
      { label: 'Light', value: 'light' },
      { label: 'Dark', value: 'dark' },
      { label: 'Monokai', value: 'monokai' },
    ],
    [],
  );

  return (
    <div className="af-property-editor__section api-response-viewer-editor">
      <h3 className="af-property-editor__section-title">API Response Viewer Properties</h3>

      <NumberPropertyEditor
        label="JSON Indentation (spaces)"
        value={config.jsonIndentation || 2}
        onChange={handleJsonIndentationChange}
        min={0}
        max={8}
        step={1}
        helpText="Number of spaces for JSON indentation"
      />

      <CheckboxPropertyEditor
        label="Show Line Numbers"
        value={config.showLineNumbers ?? true}
        onChange={handleShowLineNumbersChange}
        helpText="Display line numbers in response view"
      />

      <CheckboxPropertyEditor
        label="Show Search Box"
        value={config.showSearchBox ?? true}
        onChange={handleShowSearchBoxChange}
        helpText="Enable search/filter functionality"
      />

      <SelectPropertyEditor
        label="Syntax Highlighting Theme"
        value={config.theme || 'light'}
        onChange={handleThemeChange}
        options={themeOptions}
        helpText="Visual theme for code highlighting"
      />

      <CheckboxPropertyEditor
        label="Collapse Large Objects"
        value={config.collapseLargeObjects || false}
        onChange={handleCollapseLargeObjectsChange}
        helpText="Automatically collapse large nested objects"
      />

      <ColorPropertyEditor
        label="Background Color"
        value={config.backgroundColor || '#FFFFFF'}
        onChange={handleBackgroundColorChange}
        helpText="Response viewer background color"
      />

      <ColorPropertyEditor
        label="Text Color"
        value={config.textColor || '#000000'}
        onChange={handleTextColorChange}
        helpText="Default text color"
      />
    </div>
  );
};

ApiResponseViewerEditorComponent.displayName = 'ApiResponseViewerEditor';

/**
 * API Response Viewer Property Editor Registration
 */
export const ApiResponseViewerEditor: PropertyEditor = {
  type: 'api-response-viewer',
  label: 'API Response Viewer',
  description: 'Configure API response display, formatting, and code highlighting',
  version: '1.0.0',
  render: (props) => <ApiResponseViewerEditorComponent {...props} />,
};

export default ApiResponseViewerEditor;
